import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { View, StyleSheet } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import AddressSearchManualStep, {
  type AddressSearchManualStepHandle,
} from "../../../common/AddressSearchManualStep";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  /** Wired by MainReceiveScreen to `handleNext` (runs imperative `submit()` before advancing). */
  onContinue?: () => void;
  testID?: string;
};

export type { AddressSearchManualStepHandle as ReceiverAddressStepHandle };

const ReceiverAddressStep = forwardRef<AddressSearchManualStepHandle, Props>(
  ({ onValidationChange, onContinue, testID }, ref) => {
    const { t } = useTranslation();

    const gffpFieldsByCategory = useSessionStore(
      (s: SessionState) => (s as any).gffpFieldsByCategory
    );
    const receiverInfoFields: { XmlTag?: string; xmlTag?: string; FieldLabel?: string; fieldLabel?: string }[] =
      gffpFieldsByCategory?.["Receiver Information"] || [];

    const getFieldLabel = (tag: string): string | undefined => {
      console.log("receiverInfoFields==>", tag,receiverInfoFields)
      const field = receiverInfoFields.find(
        (f) => (f.XmlTag ?? f.xmlTag) === tag
      );
      console.log('Field==>', field)
      return field?.FieldLabel ?? field?.fieldLabel;
    };

    const receiverAddress =
      useSessionStore((s: SessionState) => (s as any).receiverAddress) || "";
    const receiverCity =
      useSessionStore((s: SessionState) => (s as any).receiverCity) || "";
    const receiverZipCode =
      useSessionStore((s: SessionState) => (s as any).receiverZipCode) || "";
    const receiverState =
      useSessionStore((s: SessionState) => (s as any).receiverState) || "";
    const setReceiverAddress = useSessionStore(
      (s: SessionState) => (s as any).setReceiverAddress
    );
    const setReceiverCity = useSessionStore(
      (s: SessionState) => (s as any).setReceiverCity
    );
    const setReceiverZipCode = useSessionStore(
      (s: SessionState) => (s as any).setReceiverZipCode
    );
    const setReceiverState = useSessionStore(
      (s: SessionState) => (s as any).setReceiverState
    );

    const bindings = useMemo(
      () => ({
        address: receiverAddress,
        city: receiverCity,
        zipCode: receiverZipCode,
        state: receiverState,
        setAddress: (value: string) => setReceiverAddress?.(value),
        setCity: (value: string) => setReceiverCity?.(value),
        setZipCode: (value: string) => setReceiverZipCode?.(value),
        setState: (value: string) => setReceiverState?.(value),
      }),
      [
        receiverAddress,
        receiverCity,
        receiverZipCode,
        receiverState,
        setReceiverAddress,
        setReceiverCity,
        setReceiverZipCode,
        setReceiverState,
      ]
    );

    const labels = useMemo(
      () => ({
        line1: getFieldLabel("receiverAddress") || t("ADDRESS"),
        line2: t("SENDER_ADDRESS_LINE2_PLACEHOLDER"),
        city:
          getFieldLabel("receiverCity") || t("SENDER_ADDRESS_CITY_PLACEHOLDER"),
        postal:
          getFieldLabel("receiverZipCode") ||
          t("SENDER_ADDRESS_POSTAL_PLACEHOLDER"),
        state:
          getFieldLabel("receiverState") || t("SENDER_ADDRESS_STATE_PLACEHOLDER"),
      }),
      [gffpFieldsByCategory, t]
    );

    const testIds = useMemo(
      () => ({
        search: "receiverAddressSearch",
        suggestion: "receiverAddressSuggestion",
        enterManually: "enterAddressManually",
        searchInstead: "searchAddressInstead",
        address: "receiverAddress",
        address2: "receiverAddress2",
        city: "receiverCity",
        zip: "receiverZipCode",
        state: "receiverState",
        stateOption: "stateOption",
        continue: "continueButton",
      }),
      []
    );

    return (
      <View style={styles.pressableRoot}>
        <AddressSearchManualStep
          ref={ref}
          testID={testID}
          bindings={bindings}
          labels={labels}
          testIds={testIds}
          onValidationChange={onValidationChange}
          onContinue={onContinue}
        />
      </View>
    );
  }
);

ReceiverAddressStep.displayName = "ReceiverAddressStep";

const styles = StyleSheet.create({
  pressableRoot: {
    flex: 1,
    width: "100%",
  },
});

export default ReceiverAddressStep;
